import { FocusTrap } from 'focus-trap-react';
import * as React from 'react';
import { CSSTransition } from 'react-transition-group';

import { Portal } from '@xing-com/portal';

import { PopUpWindowPrimitive } from './pop-up-window.primitive';
import * as Styled from './pop-up-window.styles';
import {
  dimmerDuration,
  dimmerTransitionName,
  popUpWindowDuration,
  popUpWindowTransitionName,
} from './pop-up-window.styles';
import type { PopUpWindowProps } from './pop-up-window.types';

export const PopUpWindow = React.forwardRef<HTMLDivElement, PopUpWindowProps>(
  (
    {
      children,
      className,
      closeButtonProps,
      focusTrapProps,
      hide,
      isShown,
      noPadding,
      onDimmerClick,
      ...props
    },
    forwardedRef
  ): JSX.Element => {
    const popUpWindowRef = React.useRef<HTMLDivElement>(null);
    const resolvedRef = forwardedRef || popUpWindowRef;

    const dimmerRef = React.useRef<HTMLDivElement>(null);

    // Prevent scrolling when popup is open
    React.useEffect(() => {
      if (isShown) {
        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        return () => {
          document.body.style.overflow = originalOverflow;
        };
      }

      return undefined;
    }, [isShown]);

    // Keyboard handling
    React.useEffect(() => {
      const handleKey = (event: KeyboardEvent): void => {
        if (isShown && hide && event.key === 'Escape') {
          event.preventDefault();
          hide();
        }
      };

      if (isShown) {
        document.addEventListener('keydown', handleKey);
      }

      return () => {
        document.removeEventListener('keydown', handleKey);
      };
    }, [hide, isShown]);

    const { focusTrapOptions: userFocusTrapOptions, ...otherFocusTrapProps } =
      focusTrapProps || {};
    const mergedFocusTrapOptions = {
      allowOutsideClick: true,
      clickOutsideDeactivates: true,
      escapeDeactivates: Boolean(hide),
      fallbackFocus: () => {
        // If there is no focusable element, focus the container itself
        const container =
          resolvedRef instanceof Function ? null : resolvedRef.current;
        if (container) return container;
        return document.body;
      },
      returnFocusOnDeactivate: true,
      ...(userFocusTrapOptions || {}),
    };

    const PopUpWindowTransition = (
      <Styled.Wrapper
        aria-modal="true"
        className={className}
        role="dialog"
        tabIndex={-1}
        {...props}
      >
        <CSSTransition
          appear={isShown}
          classNames={popUpWindowTransitionName}
          in={isShown}
          key="popUpWindow"
          mountOnEnter
          timeout={parseInt(popUpWindowDuration)}
          unmountOnExit
          nodeRef={resolvedRef}
        >
          <FocusTrap
            active={isShown}
            {...otherFocusTrapProps}
            focusTrapOptions={mergedFocusTrapOptions}
          >
            <PopUpWindowPrimitive
              closeButtonProps={closeButtonProps}
              hide={hide}
              noPadding={noPadding}
              ref={resolvedRef}
            >
              {children}
            </PopUpWindowPrimitive>
          </FocusTrap>
        </CSSTransition>
      </Styled.Wrapper>
    );

    const DimmerTransition = (
      <CSSTransition
        appear={isShown}
        classNames={dimmerTransitionName}
        in={isShown}
        key="dimmer"
        mountOnEnter
        timeout={parseInt(dimmerDuration)}
        unmountOnExit
        nodeRef={dimmerRef}
      >
        <Styled.Dimmer
          data-qa="pop-up-window-dimmer"
          data-testid="pop-up-window-dimmer"
          onClick={onDimmerClick}
          ref={dimmerRef}
        />
      </CSSTransition>
    );

    return (
      <>
        <Portal>{PopUpWindowTransition}</Portal>
        {DimmerTransition}
      </>
    );
  }
);

PopUpWindow.displayName = 'PopUpWindow';
